import { useCallback, useEffect, useState } from 'react'
import { extractErrorMessage, success, failure } from 'utils/errorHandling'
import useInnovaAxios from 'components/common/hooks/useInnovaAxios'
import useUnits from 'components/common/hooks/useUnits'

const groups = ['NONE', 'OFFICE', 'FIELD', 'DD', 'MWD', 'ADMIN', 'OTHER']

export function useUsers() {
  const getUsersRequest = useInnovaAxios({ url: '/admin/users' })
  const deleteUserRequest = useInnovaAxios({ url: '/admin/deleteUser' })
  const createUserRequest = useInnovaAxios({ url: '/admin/createUser' })
  const getOrgDataRequest = useInnovaAxios({ url: '/admin/getOrgListByUserAuthType' })
  const getRolesRequest = useInnovaAxios({ url: '/admin/user/roles' })
  const updateUserRequest = useInnovaAxios({ url: '/admin/updateUser' })
  const updateUserMfaRequest = useInnovaAxios({ url: '/admin/setupUserMfa' })
  const resetUserMfaRequest = useInnovaAxios({ url: '/admin/resetUserMfa' })
  const updateUserApiRequest = useInnovaAxios({ url: '/admin/setupUserApi' })
  const resetUserPwdRequest = useInnovaAxios({ url: '/admin/resetUserPwd' })
  const updateGroupSettingsRequest = useInnovaAxios({ url: '/admin/updateGroupSettings' })
  const updateUsersSettingsRequest = useInnovaAxios({ url: '/admin/updateUsersSettings' })

  const getOperators = useInnovaAxios({ url: '/admin/getOperatorList' })
  const getOperatorAccess = useInnovaAxios({ url: '/admin/getOperatorAccess' })
  const updateOperatorAccess = useInnovaAxios({ url: '/admin/updateOperatorAccess' })

  const getWells = useInnovaAxios({ url: '/admin/getWellList' })
  const getWellAccess = useInnovaAxios({ url: '/admin/getWellAccess' })
  const updateAccessWells = useInnovaAxios({ url: '/admin/updateWellAccess' })

  const [users, setUsers] = useState([])
  const [orgList, setOrgList] = useState([])
  const [roleList, setRoleList] = useState([])

  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isTogglingMfa, setIsTogglingMfa] = useState(false)
  const [isResettingMfa, setIsResettingMfa] = useState(false)
  const [isTogglingApi, setIsTogglingApi] = useState(false)
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [isLoadingAccess, setIsLoadingAccess] = useState(false)
  const [isSavingAccess, setIsSavingAccess] = useState(false)
  const [isUpdatingGroupSettings, setIsUpdatingGroupSettings] = useState(false)
  const [isUpdatingUsersSettings, setIsUpdatingUsersSettings] = useState(false)

  const { unitList } = useUnits()

  /* ============================================================
   Operator Access
   ============================================================ */

  const fetchOperatorsForUser = useCallback(
    async (user) => {
      if (!user?.userName) return failure('Invalid user')

      setIsLoadingAccess(true)

      try {
        const res = await getOperators({
          accessUser: user.userName,
          databaseUser: user.organization,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch operators'))
        }

        return success('Operators fetched', res.data ?? [])
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsLoadingAccess(false)
      }
    },
    [getOperators],
  )

  const fetchOperatorAccess = useCallback(
    async (user) => {
      if (!user?.userName) return failure('Invalid user')

      setIsLoadingAccess(true)

      try {
        const res = await getOperatorAccess({
          accessUser: user.userName,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch operator access'))
        }

        return success('Operator access fetched', res.data ?? [])
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsLoadingAccess(false)
      }
    },
    [getOperatorAccess],
  )

  const saveOperatorAccess = useCallback(
    async (user, list) => {
      if (!user?.userName) return failure('Invalid user')
      if (!Array.isArray(list)) return failure('Invalid operator list')
      if (isSavingAccess) return failure('Operation already in progress')

      setIsSavingAccess(true)

      try {
        const res = await updateOperatorAccess({
          accessUser: user.userName,
          operatorList: list.join('|'),
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update operator access'))
        }

        return success('Operator access updated')
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsSavingAccess(false)
      }
    },
    [updateOperatorAccess, isSavingAccess],
  )

  /* ============================================================
   Well Access
   ============================================================ */

  const fetchWellsForUser = useCallback(
    async (user) => {
      if (!user?.userName) return failure('Invalid user')

      setIsLoadingAccess(true)

      try {
        const res = await getWells({
          accessUser: user.userName,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch wells'))
        }

        return success('Wells fetched', res.data ?? [])
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsLoadingAccess(false)
      }
    },
    [getWells],
  )

  const fetchWellAccess = useCallback(
    async (user) => {
      if (!user?.userName) return failure('Invalid user')

      setIsLoadingAccess(true)

      try {
        const res = await getWellAccess({
          accessUser: user.userName,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch well access'))
        }

        return success('Well access fetched', res.data ?? [])
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsLoadingAccess(false)
      }
    },
    [getWellAccess],
  )

  const saveWellAccess = useCallback(
    async (user, list) => {
      if (!user?.userName) return failure('Invalid user')
      if (!Array.isArray(list)) return failure('Invalid well list')
      if (isSavingAccess) return failure('Operation already in progress')

      setIsSavingAccess(true)

      try {
        const res = await updateAccessWells({
          accessUser: user.userName,
          wellList: list.join('|'),
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update well access'))
        }

        return success('Well access updated')
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsSavingAccess(false)
      }
    },
    [updateAccessWells, isSavingAccess],
  )

  /* ============================================================
     Fetch Users
     ============================================================ */

  const updateGroupSettings = useCallback(
    async (groupSettings) => {
      if (!groupSettings) return failure('Invalid group settings')
      if (isUpdatingGroupSettings) return failure('Operation already in progress')

      setIsUpdatingGroupSettings(true)

      try {
        const res = await updateGroupSettingsRequest(groupSettings)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update group settings'))
        }

        const updatedUsers = Array.isArray(res?.data) ? res.data : []

        if (updatedUsers.length > 0) {
          setUsers((prev) => {
            const updateMap = new Map(updatedUsers.map((u) => [u.userName, u]))

            const merged = prev.map((u) => (updateMap.has(u.userName) ? { ...u, ...updateMap.get(u.userName) } : u))

            // Optional: re-sort to maintain consistency
            return merged.sort(
              (a, b) =>
                (a.organization ?? '').localeCompare(b.organization ?? '') ||
                (a.userName ?? '').localeCompare(b.userName ?? ''),
            )
          })
        }

        return success('Group settings updated', updatedUsers)
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsUpdatingGroupSettings(false)
      }
    },
    [updateGroupSettingsRequest, isUpdatingGroupSettings],
  )

  /* ============================================================
     Fetch Users
     ============================================================ */

  const fetchUsers = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await getUsersRequest()

      if (res?.error) {
        return failure(extractErrorMessage(res.error, 'Failed to fetch users'))
      }

      if (Array.isArray(res?.data)) {
        const sorted = [...res.data].sort(
          (a, b) => a.organization.localeCompare(b.organization) || a.userName.localeCompare(b.userName),
        )
        setUsers(sorted)
      }

      return success('Users loaded')
    } finally {
      setIsLoading(false)
    }
  }, [getUsersRequest])

  /* ============================================================
     Toggle MFA
     ============================================================ */

  const toggleUserMfa = useCallback(
    async (user) => {
      if (!user?.userName) return failure('Invalid user')
      if (user.authType !== 'Innova') return failure('Cannot change for non Innova auth')

      const currentApi = user.apiEnabled === true || user.apiEnabled === 1 || user.apiEnabled === '1'

      if (currentApi) return failure('Cannot change MFA for an API user')
      if (isTogglingMfa) return failure('Operation already in progress')

      setIsTogglingMfa(true)

      try {
        const currentMfa = user.mfaEnabled === true || user.mfaEnabled === 1 || user.mfaEnabled === '1'

        const nextValue = !currentMfa

        const res = await updateUserMfaRequest({
          authUserName: user.userName,
          use_mfa: nextValue,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update MFA'))
        }

        // Update users list
        setUsers((prev) => prev.map((u) => (u.userName === user.userName ? { ...u, mfaEnabled: nextValue } : u)))

        return success(`${user.userName} MFA ${nextValue ? 'enabled' : 'disabled'}`, { newValue: nextValue })
      } finally {
        setIsTogglingMfa(false)
      }
    },
    [updateUserMfaRequest, isTogglingMfa],
  )

  /* ============================================================
     Toggle API
     ============================================================ */

  const toggleUserApi = useCallback(
    async (user) => {
      if (!user?.userName) return failure('Invalid user')
      if (user.authType !== 'Innova') return failure('Cannot change for non Innova auth')
      if (isTogglingApi) return failure('Operation already in progress')

      setIsTogglingApi(true)

      try {
        const currentApi = user.apiEnabled === true || user.apiEnabled === 1 || user.apiEnabled === '1'

        const nextApi = !currentApi

        const res = await updateUserApiRequest({
          authUserName: user.userName,
          apiEnabled: nextApi,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update API access'))
        }

        // If enabling API, MFA must be disabled
        const nextMfa = nextApi ? false : user.mfaEnabled

        setUsers((prev) =>
          prev.map((u) =>
            u.userName === user.userName
              ? {
                  ...u,
                  apiEnabled: nextApi,
                  mfaEnabled: nextMfa,
                }
              : u,
          ),
        )

        return success(`${user.userName} API ${nextApi ? 'enabled' : 'disabled'}`, {
          newApiValue: nextApi,
          newMfaValue: nextMfa,
        })
      } finally {
        setIsTogglingApi(false)
      }
    },
    [updateUserApiRequest, isTogglingApi],
  )

  /* ============================================================
     Reset MFA Device
     ============================================================ */

  const resetUserMfaDevice = useCallback(
    async (user) => {
      if (!user?.userName) return failure('Invalid user')
      if (user.authType !== 'Innova') return failure('Cannot reset for non Innova auth')
      if (!user.mfaEnabled) return failure('MFA not enabled')
      if (isResettingMfa) return failure('Operation already in progress')

      setIsResettingMfa(true)
      try {
        const res = await resetUserMfaRequest({
          authUserName: user.userName,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to reset MFA'))
        }

        return success(`${user.userName} MFA device reset`)
      } finally {
        setIsResettingMfa(false)
      }
    },
    [resetUserMfaRequest, isResettingMfa],
  )

  /* ============================================================
     Reset Password
     ============================================================ */

  const resetUserPassword = useCallback(
    async (payload) => {
      if (!payload?.authUserName) return failure('Invalid user')
      if (!payload?.password) return failure('Password required')
      if (isResettingPassword) return failure('Operation already in progress')

      setIsResettingPassword(true)
      try {
        const res = await resetUserPwdRequest({
          ...payload,
          sendEmail: true,
          incPwdInEmail: true,
          resetRequiredOnNextLogin: true,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to reset password'))
        }

        setUsers((prev) => prev.map((u) => (u.userName === payload.authUserName ? { ...u } : u)))

        return success('Password reset')
      } finally {
        setIsResettingPassword(false)
      }
    },
    [resetUserPwdRequest, isResettingPassword],
  )

  /* ============================================================
     Update User
     ============================================================ */

  const updateUser = useCallback(
    async (form) => {
      if (!form?.userName) return failure('Invalid user data')
      if (isUpdating) return failure('Update already in progress')

      setIsUpdating(true)
      try {
        const payload = { ...form, updateUserName: form.userName, email: form.userName }
        delete payload.userName

        const res = await updateUserRequest(payload)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update user'))
        }

        setUsers((prev) => prev.map((u) => (u.userName === form.userName ? { ...u, ...form } : u)))

        return success('User updated')
      } finally {
        setIsUpdating(false)
      }
    },
    [updateUserRequest, isUpdating],
  )

  /* ============================================================
     Update Multiple Users
     ============================================================ */

  const updateUsersSettings = useCallback(
    async (settings) => {
      if (!settings?.organization || !settings?.userNames) {
        return failure('Invalid selected users')
      }
      if (isUpdatingUsersSettings) return failure('Update already in progress')

      setIsUpdatingUsersSettings(true)

      try {
        const res = await updateUsersSettingsRequest(settings)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update selected users'))
        }

        if (!Array.isArray(res?.data) || res.data.length === 0) {
          return failure('No updated users returned')
        }

        const updatedUsers = res.data

        setUsers((prev) => {
          const updateMap = new Map(updatedUsers.map((user) => [user.userName, user]))

          return prev.map((user) =>
            user.organization === settings.organization && updateMap.has(user.userName)
              ? { ...user, ...updateMap.get(user.userName) }
              : user,
          )
        })

        return success('Selected users updated', updatedUsers)
      } catch (err) {
        return failure(extractErrorMessage(err))
      } finally {
        setIsUpdatingUsersSettings(false)
      }
    },
    [updateUsersSettingsRequest, isUpdatingUsersSettings],
  )

  /* ============================================================
     Delete User
     ============================================================ */

  const deleteUser = useCallback(
    async (userName) => {
      if (!userName) return failure('Invalid user name')

      const user = users.find((u) => u.userName === userName)
      if (!user) return failure('User not found')

      const orgUserCount = users.filter((u) => u.organization === user.organization).length

      if (orgUserCount <= 1) {
        return failure(`Cannot delete last user for ${user.organization}`)
      }

      setIsDeleting(true)
      try {
        const res = await deleteUserRequest({ authUserName: userName })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Delete failed'))
        }

        setUsers((prev) => prev.filter((u) => u.userName !== userName))

        return success('User deleted')
      } finally {
        setIsDeleting(false)
      }
    },
    [deleteUserRequest, users],
  )

  /* ============================================================
     Add User
     ============================================================ */

  const addUser = useCallback(
    async (form) => {
      setIsCreating(true)
      try {
        const userNameValue = form.newUserName ?? form.userName

        const isMultiUser = Boolean(form.newUserName)

        const payload = {
          ...form,
          newUserName: userNameValue,
          email: userNameValue,
        }

        // Only attach password for SINGLE Innova user
        if (!isMultiUser && form.authType === 'Innova') {
          payload.password = form.password
        }

        // For single non-Innova user
        if (!isMultiUser && form.authType !== 'Innova') {
          payload.password = 'notInnovaAuthPassword'
        }

        delete payload.userName

        const res = await createUserRequest(payload)

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to create user'))
        }

        setUsers((prev) => {
          const userNames = userNameValue?.includes('|') ? userNameValue.split('|').filter(Boolean) : [userNameValue]

          const newUsers = userNames.map((userName) => ({
            ...form,
            userName,
            createdAt: new Date().toISOString(),
            email: userName,
            password: '', // never store password in state
          }))

          return [...prev, ...newUsers].sort(
            (a, b) =>
              (a.organization ?? '').localeCompare(b.organization ?? '') ||
              (a.userName ?? '').localeCompare(b.userName ?? ''),
          )
        })

        return success('User created')
      } finally {
        setIsCreating(false)
      }
    },
    [createUserRequest],
  )

  /* ============================================================
     Utilities
     ============================================================ */

  const getAuthTypeForOrg = useCallback(
    (organization) => orgList.find((o) => o.organization === organization)?.authType || 'Innova',
    [orgList],
  )

  const isMfaEnabledForOrg = useCallback(
    (organization) => Boolean(orgList.find((o) => o.organization === organization)?.mfaEnabled),
    [orgList],
  )

  const getUnitsForOrg = useCallback(
    (organization) => unitList?.filter((u) => u.organization === organization).map((u) => u.label) ?? [],
    [unitList],
  )

  const getRolesForOrg = useCallback(
    (organization) =>
      roleList.filter((r) => r.organization === organization).sort((a, b) => a.name.localeCompare(b.name)),
    [roleList],
  )

  const getAlertUsers = useCallback(() => {
    return users.filter((u) => u?.alerts?.notifications != null)
  }, [users])

  const getUserNames = useCallback(() => {
    return users.map((u) => u.userName)
  }, [users])

  /* ============================================================
     Initial Load
     ============================================================ */

  useEffect(() => {
    fetchUsers()
    fetchOrganizations()
    fetchRoles()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrganizations = async () => {
    const res = await getOrgDataRequest()
    if (!res?.error && Array.isArray(res?.data)) {
      setOrgList([...res.data].sort((a, b) => a.organization.localeCompare(b.organization)))
    }
  }

  const fetchRoles = async () => {
    const res = await getRolesRequest()
    if (!res?.error && Array.isArray(res?.data)) {
      setRoleList(res.data)
    }
  }

  return {
    users,
    orgList,
    roleList,
    unitList,
    groups,

    fetchUsers,
    deleteUser,
    addUser,
    updateUser,
    updateUsersSettings,

    toggleUserMfa,
    resetUserMfaDevice,
    toggleUserApi,
    resetUserPassword,

    getAuthTypeForOrg,
    getRolesForOrg,
    isMfaEnabledForOrg,
    getUnitsForOrg,
    getAlertUsers,
    getUserNames,

    updateGroupSettings,

    fetchOperatorsForUser,
    fetchOperatorAccess,
    saveOperatorAccess,
    fetchWellsForUser,
    fetchWellAccess,
    saveWellAccess,

    isLoading,
    isCreating,
    isUpdating,
    isUpdatingUsersSettings,
    isDeleting,
    isTogglingMfa,
    isResettingMfa,
    isTogglingApi,
    isResettingPassword,
    isLoadingAccess,
    isUpdatingGroupSettings,
  }
}

export default useUsers
